<template>
  <v-card>
    <v-card-title>
      <a @click="$emit('close')" class="close-button" title="Close">
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.634766 14.8535L6.92773 8.4375L0.898438 2.28516L2.28711 0.878906L8.29883 7.03125L14.3281 0.878906L15.7168 2.28516L9.6875 8.4375L15.9805 14.8535L14.5918 16.2422L8.29883 9.84375L2.02344 16.2422L0.634766 14.8535Z"
            fill="#155EAD"
          />
        </svg>
      </a>
    </v-card-title>
    <v-card-text>
      <div class="inner-wrap">
        <div class="help-menu">
          <a class="help-menu-item" @click="scrollTo('intro')">Introduction</a>
          <a class="help-menu-item sub-item" @click="scrollTo('not-cover')">Not yet in scope</a>
          <a class="help-menu-item" @click="scrollTo('how-to-use')">How to use</a>
          <a class="help-menu-item sub-item" @click="scrollTo('isolating-companies')">
            Isolating Companies
            <br />and related Projects
          </a>
          <a class="help-menu-item" @click="scrollTo('the-data')">The data</a>
          <a class="help-menu-item sub-item" @click="scrollTo('the-data')">Corporate Data</a>
          <a class="help-menu-item sub-item" @click="scrollTo('sources')">Data Sources</a>
          <a class="help-menu-item sub-item" @click="scrollTo('dev-projects')">Development projects</a>
          <a class="help-menu-item" @click="scrollTo('to-get')">How to get on the map</a>
          <a class="help-menu-item" @click="scrollTo('future-work')">Future work</a>
          <a class="help-menu-item" @click="scrollTo('notices')">Notices and Legal</a>
        </div>
        <div class="content-wrap">
          <section ref="intro">
            <h3 class="title">Introducing the African Influencers for Development Business Map</h3>
            <p>
              This pilot “African Influencers for Development Business Map (‘The Map‘)” is a demonstrator that explores
              how companies through their actions or business model can either affect or be positively influenced by the UNDP Sustainable
              Development Goals (SDG).
            </p>

            <p>
              The current view provides a concrete example of corporate and
              development data being utilised in a way that will assist
              development focused businesses:
            </p>
            <ul>
              <li>being identified, promoted and assisted by suitable programs</li>
              <li>
                working synergistically across Africa to achieve development
                outcomes
              </li>
            </ul>

            <p>
              From the current data-set, further opportunities both
              evolutionary and revolutionary are possible.
            </p>

            <h5 class="subtitle-1" ref="not-cover">Not yet in scope</h5>

            <p>
              In the tightly controlled scope of the pilot this map is
              <i>not</i>
              comprehensive in coverage of companies, sectors or countries.
              Also this map is not a fully featured analytics tool. Only one view of many suggestions is currently implemented.
            </p>
          </section>

          <section ref="how-to-use">
            <h3 class="title">How to use</h3>
            <p>
              Companies are plotted over a color coded map that indicates the reach of development projects.
              You can use the filters to explore specific correlations in more depth.
              Clicking on a Company or Project in the list or on the map will provide further details on that item.
            </p>
            <h5
              class="subtitle-1"
              ref="isolating-companies"
            >Isolating Companies and related Projects</h5>
            <p>
              This map dynamically recalculates both the lists and charts
              based on the current filter set. To use, simply zoom in on the location
              of interest and set any filters to isolate topic of interest.
            </p>

            <p>
              Filters that affect both which Projects and Companies displayed
              are:
            </p>
            <ul>
              <li>Portion of map visible</li>
              <li>SDG selected</li>
              <li>Name</li>
            </ul>
            <p>Filters that affect only which Companies will be displayed are:</p>
            <ul>
              <li>Sector</li>
              <li>Revenue size</li>
              <li>Employees</li>
            </ul>
            <p>
              To restart use “Clear Filters” which will reset all selection
              criteria and thus include all data again.
            </p>
          </section>

          <section ref="the-data">
            <h3 class="title">The data</h3>
            <h5 class="subtitle-1" ref="corp-data">Corporate Data</h5>

            <p>
              Data on companies in this map has been collected by Asoko
              Insight Limited on behalf of UNDP Regional Bureau for Africa.
              This data represents a snapshot of companies taken between May and June 2022.
            </p>

            <p>Criteria for including Companies on the map:</p>
            <ul>
              <li>Countries: Nigeria, Ethiopia, DRC Congo, Senegal, Mauritius</li>
              <li>Sectors: Agriculture, Financial Services, Technology</li>
              <li>
                One of the following characteristics
                <ul>
                  <li>Over $25m in annual revenues with strong links to SDGs</li>
                  <li>Tech-enabled startups with strong links to SDGs</li>
                  <li>SMEs with strong links to SDGs</li>
                </ul>
              </li>
            </ul>

            <h5 class="subtitle-1" ref="sources">Data Sources</h5>

            <p>Criteria for tagging a company with a specific SDG:</p>

            <ul>
              <li>
                Publicly sourced information, including company websites, news
                articles and social media feeds are used to determine a
                company’s contribution to a particular SDG. Specific SDG
                indicator definitions are available here:
                <a
                  href="https://unstats.un.org/sdgs/indicators/indicators-list"
                  target="_blank"
                >https://unstats.un.org/sdgs/indicators/indicators-list</a>
              </li>
            </ul>

            <h5 class="subtitle-1" ref="dev-projects">Development projects</h5>
            <p>
              The website includes development projects that were conducted by
              UNDP and their partners as a background of corporate activity.
              The data included here is a static snapshot of all projects
              taken from
              <a
                href="https://open.undp.org/projects"
              >https://open.undp.org/projects</a>
              for all african countries in 2019.
            </p>
          </section>

          <section ref="to-get">
            <h3 class="title">How to get on the map</h3>
            <p>
              African business is fast moving and comprise many young, dynamic companies
              that can have a disrupting effect and influence the UNDP SDGs in novel ways that
              are disproportionate in measure to their current size. Also more established
              firms may develop new initiatives with increased community impact.
            </p>

            <p>
              Thus we expect to find that some companies come to prominence that
              may have been overlooked in the limited scope in the first round of data collection.
            </p>

            <p>In future developments you will be able to directly engage with the map.</p>
          </section>

          <section ref="future-work">
            <h3 class="title">Future work</h3>
            <p>
              This pilot provides concrete example that explores opportunities
              that can be made possible by having corporate data available in
              the context of development planning and execution.
            </p>
            <p>
              While there is excellent work in the collection and analytics of
              macro data by the United Nations Statistics Division and others
              there is an opportunity where the “last mile” corporate data
              will enable companies across Africa to increase both their
              organic and targeted impacts on UNDP SDGs.
            </p>
            <p>
              Clearly increased coverage and extended analytic facilities are
              a logical direction of travel for this work. In addition there
              already have been exciting proposals to extend this effort with
              complementary data sets and targeted outcomes.
            </p>
          </section>

          <section ref="notices">
            <h3 class="title">Notices and Legal</h3>
            <p>
              Both the visualization and the data was commissioned from Asoko
              Insight Limited by the UNDP Regional Bureau for Africa.
            </p>
            <p>
              The designations employed and the presentation of material on
              this map do not imply the expression of any opinion whatsoever
              on the part of the Secretariat of the United Nations or UNDP
              concerning the legal status of any country, territory, city or
              area or its authorities, or concerning the delimitation of its
              frontiers or boundaries.
            </p>
          </section>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'help-content',
  data() {
    return {
      selected: 1,
    }
  },
  methods: {
    scrollTo(target) {
      this.$refs[target].scrollIntoView({
        behavior: 'smooth',
      })
    },
    scrollToDataSourcesDisclaimer() {
      this.scrollTo('sources')
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/_mixins';
.v-card {
  padding: 15px 0;
  section {
    margin-bottom: 30px;
  }
  .v-card__title {
    height: 0;
    padding: 0 !important;
    position: relative;
    .close-button {
      display: block;
      position: absolute;
      right: 26px;
      top: 1px;
    }
  }
  .v-card__text {
    padding-right: 50px !important;
  }
}
.headline {
  position: fixed;
}
.title {
  margin-bottom: 10px;
  color: #212121;
}
.subtitle-1 {
  color: #616161;
}
.help-menu {
  position: fixed;
  width: 250px;
  padding-right: 10px;
  display: block;
  @include narrower-than-desktop {
    display: none;
  }
}
.content-wrap {
  margin-left: 250px;
  @include narrower-than-desktop {
    margin-left: 0;
  }
  ul {
    margin-bottom: 20px;
  }
}
.help-menu-item {
  color: #616161;
  padding: 5px 10px;
  margin-top: 5px;
  display: block;
  cursor: pointer;
  white-space: nowrap;
  &:first-child {
    margin-top: 0px;
  }
  &.sub-item {
    padding-left: 25px;
    margin-top: 0;
  }
  &:hover {
    color: #212121;
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
